/**
 * @description Generic modal component rendering using portals
 * @example
 <Panel
 showClose={true}
 title="Title of the panel"
 activator={({ setShow }) => (
          <Button
            click={() => setShow(true)}
          >
            <span>
              Panel Tests
            </span>
          </Button>
        )}
 >
 <span> Content of the panel </span>
 </Panel>
 * @author Camilo Ramirez
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { AnimatePresence } from 'framer-motion';
import { navigate } from 'gatsby';
import Text from '../../General/Text';
import closeIcon from '../../../images/ui/panel-close.svg';
import {
  PanelBody,
  PanelClose,
  PanelHeader,
  PanelOverlay,
  PanelTitle,
  PanelTransitions,
  PanelWrapper,
} from './styles/PanelStyled';
import PageContext from '../../../contextProvider/PageContext';

const hideScroll = hide => {
  if (typeof document !== 'undefined') {
    if (hide) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.position = 'initial';
      document.body.style.width = 'auto';
    }
  }
};

const Panel = ({
  activator,
  children,
  closeCallback,
  icon,
  isMobileDevice,
  maxWidth,
  panelTitleContent,
  showByDefault,
  showClose,
  subtitle,
  title,
  panelOverlay
}) => {
  const { location } = useContext(PageContext);

  const ref = useRef();
  const [show, setShow] = useState(false);
  useEffect(() => {
    hideScroll(show);
    return () => hideScroll(false);
  }, [show]);

  const portalRoot =
    typeof document !== 'undefined' ? document.getElementById('portal') : null;

  useEffect(() => {
    if (showByDefault && !show) {
      setShow(true);
    }
  }, []);

  const handleClose = () => {
    const searchParams = new URLSearchParams(location.search);
    setShow(false);
    if (searchParams.get('modal')) navigate(location.pathname);
    closeCallback();
  };

  const content = (
    <AnimatePresence>
      {show && (
        <PanelOverlay panelOverlay={panelOverlay}>
          <PanelWrapper
            maxWidth={maxWidth}
            ref={ref}
            variants={PanelTransitions}
            initial={isMobileDevice ? 'initialMobile' : 'initial'}
            animate={isMobileDevice ? 'enterMobile' : 'enter'}
            exit={isMobileDevice ? 'exitMobile' : 'exit'}
          >
            <PanelHeader>
              <PanelTitle>
                {icon && <img src={icon} alt="Panel Icon" />}
                <div>
                  {title !== '' && (
                    <Text text={title} color="white" weight="700" size="16px" />
                  )}
                  {subtitle !== '' && (
                    <Text
                      text={subtitle}
                      color="#BFBFCF"
                      weight="400"
                      size="16px"
                      margin="5px 0 0 0"
                    />
                  )}
                  {panelTitleContent}
                </div>
              </PanelTitle>
              {showClose && (
                <PanelClose
                  type="button"
                  onClick={() => handleClose()}
                  data-testid="panel-close"
                >
                  <img src={closeIcon} alt="Icono cerrar" />
                </PanelClose>
              )}
            </PanelHeader>
            <PanelBody backgroundColor="#EFF1F3">
              {typeof children === 'function'
                ? children({ setShow })
                : children}
            </PanelBody>
          </PanelWrapper>
        </PanelOverlay>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {activator({ setShow, show })}
      {typeof window !== 'undefined' && createPortal(content, portalRoot)}
    </>
  );
};

Panel.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  activator: PropTypes.func.isRequired,
  isMobileDevice: PropTypes.bool.isRequired,
  showClose: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  icon: PropTypes.string,
  panelTitleContent: PropTypes.node,
  maxWidth: PropTypes.string,
  showByDefault: PropTypes.bool,
  closeCallback: PropTypes.func,
};

Panel.defaultProps = {
  showClose: true,
  title: '',
  subtitle: '',
  icon: '',
  panelTitleContent: null,
  maxWidth: '500px',
  showByDefault: false,
  closeCallback: () => null,
  panelOverlay: false
};

export default Panel;
